import { useEffect, useState, useMemo } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PSGC = "https://psgc.gitlab.io/api";

/**
 * Three cascading Province → City/Municipality → Barangay dropdowns.
 * Calls onChange({province: {code,name}, city: {code,name}, barangay: {code,name}}).
 *
 * Restriction: if a non-Camarines Sur province is picked, fires onNonCamSurSelected().
 */
const PSGCAddressDropdowns = ({ value = {}, onChange, onNonCamSurSelected, dataTestidPrefix = "psgc" }) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingC, setLoadingC] = useState(false);
  const [loadingB, setLoadingB] = useState(false);

  // 1. Load provinces (only once)
  useEffect(() => {
    let cancelled = false;
    setLoadingP(true);
    fetch(`${PSGC}/provinces/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setProvinces([...data].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => toast.error("Failed to load provinces"))
      .finally(() => !cancelled && setLoadingP(false));
    return () => { cancelled = true; };
  }, []);

  // 2. Load cities when province changes
  useEffect(() => {
    if (!value.province?.code) { setCities([]); return; }
    let cancelled = false;
    setLoadingC(true);
    fetch(`${PSGC}/provinces/${value.province.code}/cities-municipalities/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setCities([...data].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => toast.error("Failed to load cities"))
      .finally(() => !cancelled && setLoadingC(false));
    return () => { cancelled = true; };
  }, [value.province?.code]);

  // 3. Load barangays when city changes
  useEffect(() => {
    if (!value.city?.code) { setBarangays([]); return; }
    let cancelled = false;
    setLoadingB(true);
    fetch(`${PSGC}/cities-municipalities/${value.city.code}/barangays/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setBarangays([...data].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => toast.error("Failed to load barangays"))
      .finally(() => !cancelled && setLoadingB(false));
    return () => { cancelled = true; };
  }, [value.city?.code]);

  const camSurProvinceCode = useMemo(() => {
    const p = provinces.find((x) => x.name?.toLowerCase() === "camarines sur");
    return p?.code;
  }, [provinces]);

  const pickProvince = (code) => {
    const p = provinces.find((x) => x.code === code);
    onChange({ province: p ? { code: p.code, name: p.name } : null, city: null, barangay: null });
    if (p && camSurProvinceCode && p.code !== camSurProvinceCode) {
      onNonCamSurSelected?.(p.name);
    }
  };
  const pickCity = (code) => {
    const c = cities.find((x) => x.code === code);
    onChange({ ...value, city: c ? { code: c.code, name: c.name } : null, barangay: null });
  };
  const pickBarangay = (code) => {
    const b = barangays.find((x) => x.code === code);
    onChange({ ...value, barangay: b ? { code: b.code, name: b.name } : null });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:col-span-2" data-testid={`${dataTestidPrefix}-wrap`}>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Province</label>
        <Select value={value.province?.code || ""} onValueChange={pickProvince} disabled={loadingP}>
          <SelectTrigger className="pav-input mt-1.5" data-testid={`${dataTestidPrefix}-province`}>
            <SelectValue placeholder={loadingP ? "Loading…" : "Select province"} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {provinces.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">City / Municipality</label>
        <Select value={value.city?.code || ""} onValueChange={pickCity}
          disabled={loadingC || cities.length === 0}>
          <SelectTrigger className="pav-input mt-1.5" data-testid={`${dataTestidPrefix}-city`}>
            <SelectValue placeholder={loadingC ? "Loading…" : (cities.length ? "Select city" : "Pick province first")} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {cities.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Barangay</label>
        <Select value={value.barangay?.code || ""} onValueChange={pickBarangay}
          disabled={loadingB || barangays.length === 0}>
          <SelectTrigger className="pav-input mt-1.5" data-testid={`${dataTestidPrefix}-barangay`}>
            <SelectValue placeholder={loadingB ? "Loading…" : (barangays.length ? "Select barangay" : "Pick city first")} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {barangays.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {(loadingP || loadingC || loadingB) && (
        <p className="sm:col-span-3 text-xs text-neutral-500 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading address data…
        </p>
      )}
    </div>
  );
};

export default PSGCAddressDropdowns;
